// src/App.jsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { 
  useSecureAPI, 
  IPBlockedWarning, 
  secureAPICall 
} from './utils/security';
// Layout
import Header from "./components/Navbar";
import Footer from "./components/Footer";
import BackToTopButton from "./components/BackToTopButton";
import RedirectToDefaultLang from "./components/RedirectToDefaultLang";
import { trackPageView } from "./analytics";
import CookieBanner from "./components/CookieBanner";
import LanguagePicker from "./components/LanguagePicker";
// Pages
import HomePage from "./pages/HomePage";
import ArticlePage from "./pages/ArticlePage";
import CategoryPage from "./pages/CategoryPage";
import TagPage from "./pages/TagPage";
import AuthorPage from "./pages/AuthorPage";
import SearchWidget from "./pages/SearchPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ProfilePage from "./pages/ProfilePage";
import NotFoundPage from "./pages/NotFoundPage";
import SubjectsPage from "./pages/SubjectsPage";
import SubjectPage from "./pages/SubjectPage";
import ForgotPasswordPage from "./pages/ForgotPassword";
import { PrivacyPage, TermsPage, CookiesPage, SitemapPage } from "./pages/LegalPages";
import ContactPage from "./pages/contact";
// Admin
import { ThemeProvider, CssBaseline } from "@mui/material";
import adminTheme from "./admin/adminTheme";
import AdminRoutes from "./admin/AdminRoutes";
import LoginAdmin from "./admin/pages/Login";
import useAuth, { AuthProvider } from "./admin/hooks/useAuth";
import { HelmetProvider } from "react-helmet-async";

/* =========================
   Language config
========================= */
const DEFAULT_LANG = "en";
const SUPPORTED_LOCALES = ["en", "fr", "ar", "de"];

/* =========================
   Public Layout
========================= */
const PublicLayout = () => {
  const { lang: rawLang } = useParams();
  const navigate = useNavigate();
  const lang = SUPPORTED_LOCALES.includes(rawLang) ? rawLang : DEFAULT_LANG;

  useEffect(() => {
    if (!SUPPORTED_LOCALES.includes(rawLang)) {
      navigate(`/${DEFAULT_LANG}/`, { replace: true });
    }
  }, [rawLang, navigate]);

  return (
    <>
      <Header initialLang={lang} />
      <main className="container my-4 min-vh-75">
        <Outlet />
      </main>
      <Footer />
      <BackToTopButton />
    </>
  );
};

/* =========================
   Protected Admin Route
========================= 
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <div style={{ padding: 32 }}>Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return children;
};*/

/* =========================
   App
========================= */
/* =========================
   Analytics: track SPA route changes
========================= */
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null; // ماكيرندريش حتى حاجة، غير كيتتبع
}
export default function App() {
  
  return (
    <HelmetProvider>
    <AuthProvider>
      <Router>
    <AnalyticsTracker />
        <LanguagePicker />
        <CookieBanner />
        <Routes>

          {/* ================= ADMIN ================= 
          <Route
            path="/admin/login"
            element={
              <ThemeProvider theme={adminTheme}>
                <CssBaseline />
                <LoginAdmin />
              </ThemeProvider>
            }
          />

          <Route
            path="/admin/*"
            element={
              <ThemeProvider theme={adminTheme}>
                <CssBaseline />
                <ProtectedRoute>
                  <AdminRoutes />
                </ProtectedRoute>
              </ThemeProvider>
            }
          />*/}

          {/* ============ REDIRECT unprefixed URLs ============ */}
          <Route path="articles/:slug" element={<RedirectToDefaultLang />} />
          <Route path="subject/:slug" element={<RedirectToDefaultLang />} />
          <Route path="subjects" element={<RedirectToDefaultLang />} />
          <Route path="category/:slug" element={<RedirectToDefaultLang />} />
          <Route path="tag/:slug" element={<RedirectToDefaultLang />} />
          <Route path="author/:username" element={<RedirectToDefaultLang />} />
          <Route path="search" element={<RedirectToDefaultLang />} />
          <Route path="login" element={<RedirectToDefaultLang />} />
          <Route path="register" element={<RedirectToDefaultLang />} />
          <Route path="profile" element={<RedirectToDefaultLang />} />
          <Route path="forgot" element={<RedirectToDefaultLang />} />
<Route path="/privacy" element={<PrivacyPage />} />
<Route path="/terms"   element={<TermsPage />} />
<Route path="/cookies" element={<CookiesPage />} />
<Route path="/sitemap" element={<SitemapPage />} />
<Route path="contact" element={<RedirectToDefaultLang />} />

          {/* ================= PUBLIC ================= */}
          <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}/`} replace />} />

          {/* Language-prefixed routes */}
          <Route path=":lang/*" element={<PublicLayout />}>
            <Route index element={<HomePage />} />
            <Route path="articles/:slug" element={<ArticlePage />} />
            <Route path="subjects" element={<SubjectsPage />} />
            <Route path="subject/:slug" element={<SubjectPage />} />
            <Route path="category/:slug" element={<CategoryPage />} />
            <Route path="tag/:slug" element={<TagPage />} />
            <Route path="author/:username" element={<AuthorPage />} />
            <Route path="search" element={<SearchWidget />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="forgot" element={<ForgotPasswordPage />} />
            <Route path="contact" element={<ContactPage />} />

            <Route path="*" element={<NotFoundPage />} />

          </Route>

          {/* ================= FALLBACK ================= */}
          <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}/`} replace />} />

        </Routes>
      </Router>
    </AuthProvider>
    </HelmetProvider>
  );
}
